/**
 * Date and time rendering.
 *
 * The browser clock is never used to decide *what day it is* for business purposes — the branch
 * business date comes from the ledger and is displayed as served. These helpers only render.
 *
 * Ghana observes UTC+0 all year with no daylight saving, so Africa/Accra and UTC agree; the zone
 * is still named explicitly, because an audit timestamp read years later has to be unambiguous
 * about which clock produced it.
 */

export const INSTITUTION_TIME_ZONE = 'Africa/Accra'

const DISPLAY_LOCALE = 'en-GB'

const businessDateFormatter = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: INSTITUTION_TIME_ZONE,
})

const shortDateFormatter = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: INSTITUTION_TIME_ZONE,
})

const dateTimeFormatter = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: INSTITUTION_TIME_ZONE,
})

const timeFormatter = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: INSTITUTION_TIME_ZONE,
})

const parse = (value: string | Date) => (value instanceof Date ? value : new Date(value))

/** `2026-08-27` → `Thu, 27 Aug 2026`. Used for the business date and any due date. */
export const formatBusinessDate = (value: string | Date) => businessDateFormatter.format(parse(value))

/** `2026-08-27` → `27 Aug 2026`, for table cells where the weekday would be noise. */
export const formatShortDate = (value: string | Date) => shortDateFormatter.format(parse(value))

/** An instant, to the second — audit entries and postings need the seconds. */
export const formatDateTime = (value: string | Date) => dateTimeFormatter.format(parse(value))

export const formatTime = (value: string | Date) => timeFormatter.format(parse(value))
